import React from 'react';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import D3Cloud, { Word as D3Word } from 'd3-cloud';

import { CloudWord, Word } from './WordCloudContent';

interface ReusableWordCloud {
  /**
   * Optional: handles when a word is hovered
   * @param node
   * @returns void
   */
  onMouseEnter?: (node: CloudWord) => void;
  /**
   * Optional: handles when a word is no longer hovered
   * @returns void
   */
  onMouseLeave?: () => void;
  /**
   * Optional: handles when a word is clicked
   * @param word 
   * @returns void
   */
  onWordClick?: (word: CloudWord) => void;
  seed?: number;
  /**
   * Settings for the word cloud UI
   */
  settings?: Record<string, any>;
  /**
   * Words for the cloud, with each word being an object of key value props
   */
  words: Word[];
  /**
   * The container for the reusable word cloud component, as a ref.
   */
  contentRef?: MutableRefObject<HTMLDivElement>;
}

export default function ReusableWordCloud(props: ReusableWordCloud) {
  if (!props.words) { return; }

  const { spiralType, padding } = props?.settings ?? {};
  const baseSize = 6000;
  const aspectRatio = (props.contentRef?.current?.clientWidth ?? 600) / (props.contentRef?.current?.clientHeight ?? 350);
  const pseudoTrigRandom = function (seedNum: number) {
    let x = Math.sin(seedNum++) * 10000;
    let val = (x - Math.floor(x)) * 0.01;
    return val;
  };

  const [viewbox, setViewbox] = useState<Record<string, number>>({ x: 0, y: 0, width: 0, height: 0 });
  const [wordsForCloud, setWordsForCloud] = useState<CloudWord[]>();

  // Generates the Word Cloud for DOM rendering
  const wordCloud = useMemo(() => {
    const cloud = D3Cloud()
      .size([baseSize * aspectRatio, baseSize])
      .font("Roboto, sans-serif")
      .fontWeight(400)
      .fontSize((word: D3Word) => word.size || 16)
      .text((datum: D3Word) => datum.text ?? "")
      .random(() => pseudoTrigRandom(props.seed ?? 1))
      .timeInterval(10)
      .padding(padding ?? 5)
      .rotate(0)
      .spiral(spiralType ?? "archimedean")
      .words(props.words)
      .on('end', (words: CloudWord[], bounds) => {
        let top = 0;
        let left = 0;
        const height = bounds[1].y - bounds[0].y;
        const width = bounds[1].x - bounds[0].x;

        for (const word of words) {
          if (!word) continue;

          const wordLeft = (word.x ?? 0) - ((word.width ?? 0) / 2);
          const wordTop = (word.y ?? 0) - ((word.height ?? 0) / 2);

          if (wordLeft < left) { left = wordLeft; }
          if (wordTop < top) { top = wordTop; }
        }

        setViewbox({ x: left, y: top, width, height });
        setWordsForCloud(words);
      });

    return cloud;
  }, [props.words, props.seed]);

  useEffect(() => {
    if (wordCloud) {
      wordCloud.size([baseSize * aspectRatio, baseSize]);
      wordCloud.start();
    };
  }, [wordCloud, aspectRatio]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      viewBox={`${viewbox.x} ${viewbox.y} ${viewbox.width} ${viewbox.height}`}
    >
      {wordsForCloud?.map((word: CloudWord, index) => {
        return (
          <text
            key={index}
            style={{ cursor: props.onWordClick ? "pointer" : "default",  fill: word.color, fontSize: word.size }}
            textAnchor="middle"
            x={word.x}
            y={word.y}>
            <tspan onMouseLeave={() => props.onMouseLeave && props.onMouseLeave()}>
              <tspan onMouseEnter={() => props.onMouseEnter && props.onMouseEnter(word)}>
                <tspan onClick={() => props.onWordClick && props.onWordClick(word)}>
                  {word.displayText}
                </tspan>
              </tspan>
            </tspan>
          </text>
        )
      })}
    </svg>
  );
}
